import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Copy, ArrowRight, Sparkles } from "lucide-react";
import { renderSummary } from "@/lib/renderSummary";
import { PUBLIC } from "@/constants/testIds";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const PUBLIC_API = `${BACKEND_URL}/api/public`;

export default function PublicSummary() {
  const { id } = useParams();
  const [item, setItem] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    setLoading(true);
    axios
      .get(`${PUBLIC_API}/summaries/${id}`)
      .then(({ data }) => setItem(data))
      .catch((err) =>
        setError(err?.response?.data?.detail || "Summary not available")
      )
      .finally(() => setLoading(false));
  }, [id]);

  const onCopy = async () => {
    if (!item?.summary) return;
    await navigator.clipboard.writeText(item.summary);
    toast.success("Copied to clipboard");
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="sux-marker">loading…</div>
      </div>
    );
  }

  if (error || !item) {
    return (
      <div
        data-testid={PUBLIC.notFound}
        className="min-h-screen flex flex-col items-center justify-center p-8 text-center"
      >
        <div className="sux-marker mb-4">404 · unavailable</div>
        <h1 className="text-4xl font-black mb-4" style={{ fontFamily: "Outfit" }}>
          This summary is private.
        </h1>
        <p className="text-muted-foreground mb-8 max-w-md">
          The link you followed either expired or was never shared publicly.
        </p>
        <Link
          to="/"
          className="font-mono uppercase tracking-[0.25em] text-[11px] bg-[#002FA7] text-white px-6 py-3 hover:bg-[#00227A] transition-colors"
        >
          Go to SUX1 →
        </Link>
      </div>
    );
  }

  const date = new Date(item.created_at);
  const ratio = Math.round(
    (1 - item.summary_chars / Math.max(item.original_chars, 1)) * 100
  );

  return (
    <div data-testid={PUBLIC.page} className="min-h-screen bg-card">
      {/* Public header */}
      <header className="sticky top-0 z-40 bg-background/95 backdrop-blur-md border-b border-border">
        <div className="h-16 flex items-center px-6 md:px-10 justify-between">
          <Link to="/" className="flex items-center gap-3">
            <span className="inline-block w-7 h-7 bg-[#002FA7]" />
            <span
              className="font-black text-2xl tracking-tight"
              style={{ fontFamily: "Outfit" }}
            >
              SUX<span className="text-[#FF2A00]">1</span>
            </span>
          </Link>
          <Link
            to="/signup"
            data-testid={PUBLIC.cta}
            className="font-mono uppercase tracking-[0.2em] text-[11px] bg-[#002FA7] text-white px-4 py-2 hover:bg-[#00227A] transition-colors flex items-center gap-2"
          >
            <Sparkles className="w-3 h-3" /> Try SUX1 free
          </Link>
        </div>
      </header>

      <article className="max-w-3xl mx-auto px-6 md:px-8 py-12 md:py-16">
        <div className="sux-marker mb-4">
          Shared by {item.author_name} ·{" "}
          {date.toLocaleDateString(undefined, {
            year: "numeric",
            month: "long",
            day: "2-digit",
          })}
        </div>
        <h1
          className="text-4xl md:text-5xl font-black tracking-tight mb-8 leading-tight"
          style={{ fontFamily: "Outfit" }}
        >
          {item.title}
        </h1>

        <div className="grid grid-cols-2 md:grid-cols-4 border border-border mb-10">
          {[
            ["Model", item.model],
            ["Length", item.length],
            ["Format", item.format],
            ["Compression", item.original_chars < 100 || ratio < 0 ? "N/A" : `${ratio}%`],
          ].map(([k, v]) => (
            <div
              key={k}
              className="p-4 border-r border-b md:border-b-0 border-border last:border-r-0"
            >
              <div className="sux-marker mb-1">{k}</div>
              <div className="font-mono text-sm">{v}</div>
            </div>
          ))}
        </div>

        <article
          className="summary-content notranslate"
          translate="no"
          dangerouslySetInnerHTML={{ __html: renderSummary(item.summary) }}
        />

        <div className="mt-12 border-t border-border pt-6 flex items-center justify-between flex-wrap gap-4">
          <button
            onClick={onCopy}
            data-testid={PUBLIC.copy}
            className="font-mono uppercase tracking-[0.2em] text-[11px] px-4 py-2 border border-border hover:bg-[#FF2A00] hover:text-white transition-colors flex items-center gap-2"
          >
            <Copy className="w-3 h-3" /> Copy summary
          </button>
          <Link
            to="/signup"
            className="font-mono uppercase tracking-[0.2em] text-[11px] bg-[#002FA7] text-white px-4 py-2 hover:bg-[#00227A] transition-colors flex items-center gap-2"
          >
            Summarize your own text <ArrowRight className="w-3 h-3" />
          </Link>
        </div>
      </article>

      <footer className="border-t border-border py-8 px-6 md:px-10 text-center">
        <div className="sux-marker">
          Generated with <span className="text-[#002FA7]">SUX1</span> · multi-model
          text summarization
        </div>
      </footer>
    </div>
  );
}
